import axios from "axios";
import {
  setUser,
  setRecoverPassword,
  setCredentials,
  setWallet,
  resetUser,
  setChangePassword,
} from "../reducers/user";
import { encryptText, clearSession, TOKEN_KEY } from "../../shared/utils";

const URL = process.env.REACT_APP_CORE;

export const getUserPortal = (id) => async (dispatch) => {
  dispatch(
    setUser({
      data: null,
      isLoading: true,
      error: null,
    })
  );

  let url = `${URL}usuarios/${id}`;

  const configDocIden = {
    method: "GET",
    maxBodyLength: Infinity,
    url,
  };

  axios(configDocIden)
    .then((response) => {
      localStorage.setItem("userId", response?.data?.usuarioid);
      dispatch(getCredentialsPortal(response?.data?.usuarioid));
      dispatch(
        setUser({
          data: response?.data,
          isLoading: false,
          error: null,
        })
      );
    })
    .catch((error) => {
      dispatch(
        setUser({
          data: null,
          isLoading: false,
          error: error?.response?.data?.error,
        })
      );
    });
};

export const login = (username, password, otp) => async (dispatch) => {
  dispatch(setUser({ data: null, isLoading: true, error: null }));

  // El login es publico y devuelve el token de usuario (paso 2, con OTP). Ya no
  // se pide antes un token de servicio con credenciales del bundle.
  clearSession();

  const encryptedUsername = encryptText(username);
  const encryptedPassword = encryptText(password);
  const encryptedOtp = encryptText(otp);

  const config = {
    method: "POST",
    url: `${URL}login`,
    headers: {
      "Content-Type": "application/json",
    },
    data: {
      type: "GFLogin",
      user_name: encryptedUsername,
      password: encryptedPassword,
      otp_code: encryptedOtp,
    },
  };

  axios(config)
    .then(function (response) {
      if (response?.data?.error || response?.data?.message?.includes("@")) {
        dispatch(
          setUser({
            data: null,
            isLoading: false,
            error: response?.data?.error || response?.data?.message,
          })
        );
        return;
      }

      if (response?.data?.token) {
        localStorage.setItem(TOKEN_KEY, response.data.token);
      }
      dispatch(getUserPortal(response?.data?.user));
    })
    .catch(function (error) {
      return;
    });
};

export const logout = (username, password) => async (dispatch) => {
  // POST con body (la ruta /login de ClientCore es solo POST): antes iba por GET
  // con usuario y clave en la query, que terminaban en los logs, y daba 404.
  // GFLogout no usa la password.
  const token = localStorage.getItem(TOKEN_KEY);
  const config = {
    method: "POST",
    url: `${URL}login`,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    data: {
      type: "GFLogout",
      user_name: encryptText(username),
    },
  };

  // dispatch(setUser({ data: null, isLoading: true, error: null }));

  axios(config)
    .then(function (response) {
      if (response?.data?.error) {
        dispatch(
          setUser({
            data: null,
            isLoading: false,
            error: response?.data?.error,
          })
        );
        return;
      }

      dispatch(resetUser());
      // El logout borraba la clave "token", que nunca existio: la sesion
      // seguia viva en el browser. La real es "tokenContraPago".
      localStorage.removeItem("tokenContraPago");
      localStorage.removeItem("userId");
      window.location.href = "/";
    })
    .catch((error) => {
      dispatch(
        setUser({
          data: null,
          isLoading: false,
          error: error?.response?.data?.error,
        })
      );
    });
};

// Mensaje uniforme: no depende de lo que conteste el backend (exista o no la
// cuenta, la respuesta es la misma).
const RECOVERY_MESSAGE =
  "If an account exists for that email, we sent you a link to reset your password.";

export const recoverPassword = (email) => async (dispatch) => {
  dispatch(
    setRecoverPassword({
      isLoading: true,
      error: null,
      success: null,
    })
  );

  // R23: la clave "token" nunca se escribe; la unica que se setea es
  // "tokenContraPago" (shared/utils.js getToken). Antes esto mandaba
  // Authorization: Bearer null y "funcionaba" porque nadie validaba.
  const token = localStorage.getItem("tokenContraPago");
  const config = {
    method: "POST",
    url: `${URL}recoverypassword`,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    data: {
      mail: email,
    },
  };

  axios(config)
    .then(function (response) {
      dispatch(
        setRecoverPassword({
          isLoading: false,
          error: null,
          success: RECOVERY_MESSAGE,
        })
      );
    })
    .catch(function (error) {
      dispatch(
        setRecoverPassword({
          isLoading: false,
          error:
            error?.response?.status === 429
              ? "Too many attempts. Please try again later."
              : error?.response?.data?.error ||
                "Something went wrong. Please try again.",
          success: null,
        })
      );
    });
};

// Links de un solo uso que llegan por mail (reset de clave, confirmacion de
// email). Endpoints publicos de ClientCore: el token del link es la credencial.
export const getAccountLinkError = (error) => {
  const status = error?.response?.status;
  const data = error?.response?.data;
  if (status === 429) return "Too many attempts. Please try again later.";
  if (data?.code === "WEAK_PASSWORD" || data?.code === "SAME_PASSWORD") {
    return data.error;
  }
  if (status === 400) return "This link is invalid or has expired. Request a new one.";
  return "Something went wrong. Please try again.";
};

export const resetPasswordRequest = (token, newPassword) =>
  axios({
    method: "POST",
    url: `${URL}resetpassword`,
    headers: { "Content-Type": "application/json" },
    // Cifrada RSA igual que la password del login.
    data: { token, nueva_clave: encryptText(newPassword) },
  });

export const confirmEmailRequest = (token) =>
  axios({
    method: "POST",
    url: `${URL}confirmemail`,
    headers: { "Content-Type": "application/json" },
    data: { token },
  });

// Cambio de email del cliente: ClientCore responde 202 {pendiente:true} y manda
// un link de confirmacion al email nuevo (el backend anterior respondia 200 y
// lo aplicaba directo). El token de sesion lo agrega el interceptor.
export const requestEmailChange = (userId, newEmail, currentPassword) =>
  axios({
    method: "PUT",
    url: `${URL}usuarios/${userId}`,
    headers: { "Content-Type": "application/json" },
    data: {
      email: newEmail,
      clave_actual: encryptText(currentPassword),
    },
  });

export const getCredentialsPortal = (id) => async (dispatch) => {
  dispatch(
    setCredentials({
      data: null,
      isLoading: true,
      error: null,
    })
  );

  const configCredentials = {
    method: "GET",
    maxBodyLength: Infinity,
    url: `${process.env.REACT_APP_API_PORTAL}clientes?userid=${id}`,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${localStorage.getItem("tokenContraPago")}`,
    },
  };

  axios(configCredentials)
    .then((response) => {
      dispatch(getWalletPortal(response?.data?.data?.[0]?.id));
      dispatch(
        setCredentials({
          data: response?.data?.data?.[0] || null,
          isLoading: false,
          error: null,
        })
      );
    })
    .catch((error) => {
      dispatch(
        setCredentials({
          data: null,
          isLoading: false,
          error: error?.response?.data,
        })
      );
    });
};

export const getWalletPortal = (idCredential) => async (dispatch) => {
  dispatch(
    setWallet({
      data: null,
      isLoading: true,
      error: null,
    })
  );

  const configDocIden = {
    method: "GET",
    maxBodyLength: Infinity,
    url: `${process.env.REACT_APP_API_PORTAL}wallet?idclient=${idCredential}`,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${localStorage.getItem("tokenContraPago")}`,
    },
  };

  axios(configDocIden)
    .then((response) => {
      dispatch(
        setWallet({
          data: response?.data?.data?.[0] || null,
          isLoading: false,
          error: null,
        })
      );
    })
    .catch((error) => {
      dispatch(
        setWallet({
          data: null,
          isLoading: false,
          error: error?.response?.data,
        })
      );
    });
};

export const changePassword = (newPassword, userId, currentPassword) => async (dispatch) => {
  dispatch(
    setChangePassword({
      message: null,
      isLoading: true,
      error: null,
    })
  );

  // R23: la clave "token" nunca se escribe; la unica que se setea es
  // "tokenContraPago" (shared/utils.js getToken). Antes esto mandaba
  // Authorization: Bearer null y "funcionaba" porque nadie validaba.
  const token = localStorage.getItem("tokenContraPago");
  const config = {
    method: "PUT",
    url: `${URL}usuarios/${userId}`,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    // clave_actual: obligatoria para el rol cliente (403 si no coincide). Va
    // cifrada RSA igual que la password del login.
    data: {
      clave: newPassword,
      clave_actual: encryptText(currentPassword),
    },
  };

  axios(config)
    .then((response) => {
      dispatch(
        setChangePassword({
          message: response?.data?.message
            ? "Password changed successfully"
            : "",
          isLoading: false,
          error: null,
        })
      );
    })
    .catch((error) => {
      dispatch(
        setChangePassword({
          message:
            error?.response?.data?.code === "INVALID_CURRENT_PASSWORD"
              ? "Current password is incorrect"
              : error?.response?.data?.code === "WEAK_PASSWORD"
              ? "The new password must be at least 10 characters"
              : "Error changing password",
          isLoading: false,
          error: true,
        })
      );
    });
};
